import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Check, ChevronRight, Shield, AlertTriangle, Sparkles, Info } from 'lucide-react';
import { useStore } from '@/hooks/useStore';
import LoadingSpinner from '@/components/LoadingSpinner';
import PlantAvatar from '@/components/PlantAvatar';
import PhotoPicker from '@/components/PhotoPicker';
import {
  useTelegramHaptic,
  useTelegramBackButton,
  useTelegramMainButton,
  useTelegramClosingConfirmation,
  useTelegram,
} from '@/hooks/telegram';
import * as api from '@/utils/api';
import type { PlantSpecies, PlantPersonality, PlantCategory, PotSize } from '@/types';
import { categoryChips, categoryLabels, personalities, potSizes } from '@/utils/constants';

type Step = 'species' | 'details' | 'personality';

export default function AddPlantPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id);
  const { isTelegramWebApp } = useTelegram();
  const haptic = useTelegramHaptic();
  const { species, isLoadingSpecies, fetchSpecies, createPlant, updatePlant } = useStore();

  const [step, setStep] = useState<Step>('species');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PlantCategory | null>(null);
  const [filterBeginner, setFilterBeginner] = useState(false);
  const [filterPetSafe, setFilterPetSafe] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<PlantSpecies | null>(null);
  const [customSpecies, setCustomSpecies] = useState('');
  const [nickname, setNickname] = useState('');
  const [location, setLocation] = useState('');
  const [potSize, setPotSize] = useState<PotSize>('MEDIUM');
  const [personality, setPersonality] = useState<PlantPersonality>('FRIENDLY');
  const [customWateringDays, setCustomWateringDays] = useState<string>('');
  const [acquiredAt, setAcquiredAt] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPlant, setIsLoadingPlant] = useState(false);

  // Track original values for edit mode comparison
  const [originalValues, setOriginalValues] = useState<{
    nickname: string;
    location: string;
    potSize: PotSize;
    personality: PlantPersonality;
    customWateringDays: string;
    speciesId?: string;
  } | null>(null);

  // Track if user has made changes (for closing confirmation)
  const hasUnsavedChanges = isEditMode
    ? originalValues !== null && (
        nickname !== originalValues.nickname ||
        location !== originalValues.location ||
        potSize !== originalValues.potSize ||
        personality !== originalValues.personality ||
        customWateringDays !== originalValues.customWateringDays ||
        selectedSpecies?.id !== originalValues.speciesId
      )
    : (nickname.trim() !== '' || selectedSpecies !== null || customSpecies !== '');

  // Enable closing confirmation when there are unsaved changes
  useTelegramClosingConfirmation(hasUnsavedChanges);

  useEffect(() => {
    if (species.length === 0) fetchSpecies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select species from URL query param (e.g., from species detail page)
  useEffect(() => {
    const speciesId = searchParams.get('speciesId');
    if (!speciesId || isEditMode || species.length === 0) return;
    const found = species.find((s) => s.id === speciesId);
    if (found && !selectedSpecies) {
      setSelectedSpecies(found);
      setNickname(found.commonNameRu);
      setStep('details');
    }
  }, [searchParams, species, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load plant data when editing
  useEffect(() => {
    if (!id) return;

    async function loadPlant() {
      setIsLoadingPlant(true);
      try {
        const plant = await api.getPlant(id!);
        setNickname(plant.nickname);
        setLocation(plant.location || '');
        setPotSize(plant.potSize);
        setPersonality(plant.personality);
        setCustomSpecies(plant.customSpecies || '');
        setCustomWateringDays(plant.customWateringDays ? String(plant.customWateringDays) : '');
        setAcquiredAt(plant.acquiredAt ? new Date(plant.acquiredAt).toISOString().slice(0, 10) : '');
        if (plant.photoUrl) {
          setExistingPhotoUrl(plant.photoUrl);
        }
        if (plant.species) {
          setSelectedSpecies(plant.species);
        }
        // Save original values for change detection
        setOriginalValues({
          nickname: plant.nickname,
          location: plant.location || '',
          potSize: plant.potSize,
          personality: plant.personality,
          customWateringDays: plant.customWateringDays ? String(plant.customWateringDays) : '',
          speciesId: plant.species?.id,
        });
        // Skip to details step in edit mode
        setStep('details');
      } catch {
        haptic('error');
        navigate('/');
      } finally {
        setIsLoadingPlant(false);
      }
    }

    loadPlant();
  }, [id, navigate, haptic]);

  // BackButton handler
  const handleBack = useCallback(() => {
    if (step === 'details') {
      if (isEditMode) {
        navigate(`/plant/${id}`);
      } else {
        setStep('species');
      }
    } else if (step === 'personality') {
      setStep('details');
    } else {
      navigate(isEditMode ? `/plant/${id}` : '/');
    }
  }, [step, isEditMode, id, navigate]);

  // Use Telegram BackButton
  useTelegramBackButton(handleBack);

  // Species filtered by beginner/petSafe only (for category chip counts)
  const preFilteredSpecies = useMemo(() => {
    if (!filterBeginner && !filterPetSafe) return species;
    return species.filter((s) => {
      const matchesBeginner = !filterBeginner || s.difficultyLevel === 'BEGINNER';
      const matchesPetSafe = !filterPetSafe || s.petFriendly;
      return matchesBeginner && matchesPetSafe;
    });
  }, [species, filterBeginner, filterPetSafe]);

  const filteredSpecies = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = preFilteredSpecies.filter((s) => {
      const matchesCategory = !selectedCategory || s.category === selectedCategory;
      const matchesSearch = !searchQuery ||
        s.commonNameRu.toLowerCase().includes(q) ||
        s.scientificName.toLowerCase().includes(q) ||
        s.commonNameEn?.toLowerCase().includes(q) ||
        s.commonNameSynonyms?.toLowerCase().includes(q) ||
        s.tags?.some((t) => t.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
    return filtered.sort((a, b) => (b.popularity ?? 5) - (a.popularity ?? 5));
  }, [preFilteredSpecies, selectedCategory, searchQuery]);

  // Group species by category for sectioned display
  const groupedSpecies = useMemo(() => {
    if (selectedCategory || searchQuery || filterBeginner || filterPetSafe) return null;
    const groups: Record<string, PlantSpecies[]> = {};
    for (const sp of filteredSpecies) {
      if (!groups[sp.category]) groups[sp.category] = [];
      groups[sp.category].push(sp);
    }
    return groups;
  }, [filteredSpecies, selectedCategory, searchQuery, filterBeginner, filterPetSafe]);

  const handleSpeciesSelect = (sp: PlantSpecies) => {
    haptic('selection');
    setSelectedSpecies(sp);
    setNickname(sp.commonNameRu);
    setStep('details');
  };

  const handleCustomSpecies = () => {
    haptic('selection');
    setSelectedSpecies(null);
    setCustomSpecies(searchQuery);
    setNickname(searchQuery || 'Моё растение');
    setStep('details');
  };

  const handleDetailsNext = useCallback(() => {
    if (!nickname.trim()) {
      haptic('error');
      return;
    }
    haptic('selection');
    setStep('personality');
  }, [nickname, haptic]);

  const handleSubmit = useCallback(async () => {
    if (!nickname.trim()) {
      haptic('error');
      return;
    }

    haptic('medium');
    setIsSubmitting(true);

    const parsedWateringDays = customWateringDays ? parseInt(customWateringDays, 10) : undefined;
    const validWateringDays = parsedWateringDays && parsedWateringDays > 0 ? parsedWateringDays : undefined;

    try {
      if (isEditMode && id) {
        await updatePlant(id, {
          nickname: nickname.trim(),
          speciesId: selectedSpecies?.id,
          customSpecies: !selectedSpecies ? customSpecies : undefined,
          location: location.trim() || undefined,
          potSize,
          personality,
          customWateringDays: validWateringDays,
          acquiredAt: acquiredAt ? new Date(acquiredAt).toISOString() : undefined,
        });
        if (photoToDelete) {
          await api.deletePlantPhoto(id).catch(() => {});
        }
        if (photoFile) {
          try {
            await api.uploadPlantPhoto(id, photoFile);
          } catch {
            // Plant saved but photo upload failed — non-blocking
            console.error('Photo upload failed');
          }
        }
        haptic('success');
        navigate(`/plant/${id}`);
      } else {
        const plant = await createPlant({
          nickname: nickname.trim(),
          speciesId: selectedSpecies?.id,
          customSpecies: !selectedSpecies ? customSpecies : undefined,
          location: location.trim() || undefined,
          potSize,
          personality,
          customWateringDays: validWateringDays,
          acquiredAt: acquiredAt ? new Date(acquiredAt).toISOString() : undefined,
        });
        if (photoFile) {
          try {
            await api.uploadPlantPhoto(plant.id, photoFile);
          } catch {
            // Plant created but photo upload failed — non-blocking
            console.error('Photo upload failed');
          }
        }
        haptic('success');
        navigate(`/plant/${plant.id}`);
      }
    } catch {
      haptic('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [nickname, isEditMode, id, selectedSpecies, customSpecies, location, potSize, personality, customWateringDays, haptic, updatePlant, createPlant, navigate]);

  // MainButton configuration
  const mainButtonConfig = useMemo(() => {
    if (step === 'species' || isLoadingPlant) return null;

    if (step === 'details') {
      return {
        text: 'Далее',
        onClick: handleDetailsNext,
        isEnabled: nickname.trim() !== '',
      };
    }

    // step === 'personality'
    return {
      text: isEditMode ? 'Сохранить' : 'Добавить растение',
      onClick: handleSubmit,
      isEnabled: !isSubmitting,
      showProgress: isSubmitting,
    };
  }, [step, isLoadingPlant, nickname, isEditMode, isSubmitting, handleDetailsNext, handleSubmit]);

  // Use Telegram MainButton
  useTelegramMainButton(mainButtonConfig);

  if (isLoadingPlant) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col safe-area-top">
      {/* Header */}
      <div className="px-4 py-4 border-b border-[var(--sf-border-light)]">
        <div className="flex items-center gap-4">
          {!isTelegramWebApp && (
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full glass-circle flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="font-semibold text-lg">
              {isEditMode ? 'Редактировать растение' : 'Добавить растение'}
            </h1>
            <p className="text-sm text-tg-hint">
              {step === 'species' && 'Шаг 1: Выберите вид'}
              {step === 'details' && (isEditMode ? 'Детали' : 'Шаг 2: Детали')}
              {step === 'personality' && (isEditMode ? 'Характер' : 'Шаг 3: Характер')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Step 1: Species selection */}
        {step === 'species' && (
          <div>
            {/* Filter toggles */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => {
                  haptic('selection');
                  setFilterBeginner(!filterBeginner);
                }}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 inline-flex items-center gap-1 ${
                  filterBeginner
                    ? 'bg-plant-green-500 text-white'
                    : 'glass-circle text-tg-hint'
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Для новичков
              </button>
              <button
                onClick={() => {
                  haptic('selection');
                  setFilterPetSafe(!filterPetSafe);
                }}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 inline-flex items-center gap-1 ${
                  filterPetSafe
                    ? 'bg-plant-green-500 text-white'
                    : 'glass-circle text-tg-hint'
                }`}
              >
                <Shield className="w-3.5 h-3.5" />
                Безопасно для питомцев
              </button>
            </div>

            {/* Category chips */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-3 -mx-4 px-4 no-scrollbar">
              {categoryChips.map((chip) => (
                <button
                  key={chip.id ?? 'all'}
                  onClick={() => {
                    haptic('selection');
                    setSelectedCategory(chip.id);
                  }}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === chip.id
                      ? 'bg-plant-green-500 text-white'
                      : 'glass-circle text-tg-hint'
                  }`}
                >
                  {chip.label}
                  {chip.id && (
                    <span className="ml-1 opacity-70">
                      ({preFilteredSpecies.filter((s) => s.category === chip.id).length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-tg-hint" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск растения..."
                className="tg-input !pl-12"
              />
            </div>

            {/* Custom species shortcut — shown when searching */}
            {searchQuery.trim() && (
              <button
                onClick={handleCustomSpecies}
                className="w-full tg-card flex items-center gap-3 text-left active:scale-[0.98] transition-transform mb-2"
              >
                <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🪴</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">Указать как "{searchQuery}"</p>
                  <p className="text-sm text-tg-hint">Моего растения нет в списке</p>
                </div>
                <ChevronRight className="w-5 h-5 text-tg-hint" />
              </button>
            )}

            {/* Species list */}
            {isLoadingSpecies ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-2">
                {groupedSpecies ? (
                  // Grouped by category when no filter/search
                  Object.entries(groupedSpecies).map(([cat, speciesList]) => (
                    <div key={cat}>
                      <h3 className="text-sm font-medium text-tg-hint uppercase tracking-wide mt-4 mb-2 first:mt-0">
                        {categoryLabels[cat as PlantCategory] || cat} ({speciesList.length})
                      </h3>
                      {speciesList.map((sp) => (
                        <div key={sp.id} className="tg-card flex items-center gap-3 mb-2">
                          <button
                            onClick={() => handleSpeciesSelect(sp)}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.98] transition-transform"
                          >
                            <PlantAvatar
                              speciesScientificName={sp.scientificName}
                              category={sp.category}
                              size="sm"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="font-medium truncate">{sp.commonNameRu}</p>
                                {sp.toxicity === 'NONE' && (
                                  <Shield className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                )}
                                {sp.toxicity && sp.toxicity !== 'NONE' && (
                                  <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-tg-hint truncate">
                                {sp.commonNameEn ? `${sp.commonNameEn} · ` : ''}{sp.scientificName}
                              </p>
                            </div>
                          </button>
                          <button
                            onClick={() => navigate(`/species/${sp.id}`)}
                            className="w-8 h-8 rounded-full glass-circle flex items-center justify-center flex-shrink-0"
                          >
                            <Info className="w-4 h-4 text-tg-hint" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  // Flat list when filtered or searching
                  filteredSpecies.map((sp) => (
                    <div key={sp.id} className="tg-card flex items-center gap-3">
                      <button
                        onClick={() => handleSpeciesSelect(sp)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left active:scale-[0.98] transition-transform"
                      >
                        <PlantAvatar
                          speciesScientificName={sp.scientificName}
                          category={sp.category}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium truncate">{sp.commonNameRu}</p>
                            {sp.toxicity === 'NONE' && (
                              <Shield className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            )}
                            {sp.toxicity && sp.toxicity !== 'NONE' && (
                              <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-tg-hint truncate">
                            {sp.commonNameEn ? `${sp.commonNameEn} · ` : ''}{sp.scientificName}
                          </p>
                        </div>
                      </button>
                      <button
                        onClick={() => navigate(`/species/${sp.id}`)}
                        className="w-8 h-8 rounded-full glass-circle flex items-center justify-center flex-shrink-0"
                      >
                        <Info className="w-4 h-4 text-tg-hint" />
                      </button>
                    </div>
                  ))
                )}

                {/* Custom species option */}
                <button
                  onClick={handleCustomSpecies}
                  className="w-full tg-card flex items-center gap-3 text-left active:scale-[0.98] transition-transform"
                  style={{ borderStyle: 'dashed' }}
                >
                  <div className="w-10 h-10 rounded-full glass-circle flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🪴</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">
                      {searchQuery ? `"${searchQuery}"` : 'Другое растение'}
                    </p>
                    <p className="text-sm text-tg-hint">Указать свой вид</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-tg-hint" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Details */}
        {step === 'details' && (
          <div className="space-y-4">
            {/* Species info + photo */}
            <div className="tg-card flex items-center gap-3">
              <PhotoPicker
                photoUrl={photoFile ? null : existingPhotoUrl}
                onPhotoSelected={(file) => setPhotoFile(file)}
                onPhotoRemoved={() => {
                  setPhotoFile(null);
                  if (isEditMode && existingPhotoUrl) {
                    setPhotoToDelete(true);
                  }
                  setExistingPhotoUrl(null);
                }}
                size="sm"
              />
              <div className="flex-1">
                <p className="font-medium">
                  {selectedSpecies?.commonNameRu || customSpecies || 'Неизвестный вид'}
                </p>
                {selectedSpecies && (
                  <p className="text-sm text-tg-hint">{selectedSpecies.scientificName}</p>
                )}
              </div>
              <button
                onClick={() => setStep('species')}
                className="text-sm text-plant-green-500 font-medium"
              >
                Изменить
              </button>
            </div>

            {/* Nickname */}
            <div>
              <label className="block text-sm font-medium mb-2">Имя растения</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Как назовём?"
                className="tg-input"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium mb-2">Расположение</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Например: гостиная, у окна"
                className="tg-input"
              />
            </div>

            {/* Acquired date */}
            <div>
              <label className="block text-sm font-medium mb-2">Дата приобретения</label>
              <input
                type="date"
                value={acquiredAt}
                onChange={(e) => setAcquiredAt(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="tg-input"
              />
            </div>

            {/* Custom watering interval */}
            <div>
              <label className="block text-sm font-medium mb-2">Интервал полива (дней)</label>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="90"
                value={customWateringDays}
                onChange={(e) => setCustomWateringDays(e.target.value)}
                placeholder={`По умолчанию: ${selectedSpecies?.wateringFrequencyDays ?? 7} дней`}
                className="tg-input"
              />
            </div>

            {/* Pot size */}
            <div>
              <label className="block text-sm font-medium mb-2">Размер горшка</label>
              <div className="grid grid-cols-2 gap-2">
                {potSizes.map((size) => (
                  <button
                    key={size.id}
                    onClick={() => {
                      haptic('selection');
                      setPotSize(size.id);
                    }}
                    className={`p-3 rounded-xl text-left transition-all duration-200 ${
                      potSize === size.id
                        ? 'glass-circle-green border-plant-green-500/50'
                        : 'glass-circle'
                    }`}
                    style={potSize === size.id ? { borderColor: 'rgba(34, 197, 94, 0.5)' } : {}}
                  >
                    <p className="font-medium text-sm">{size.name}</p>
                    <p className="text-xs text-tg-hint">{size.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Personality */}
        {step === 'personality' && (
          <div>
            <p className="text-tg-hint mb-4">
              Выберите характер для вашего растения. Это повлияет на то, как оно будет "общаться" с вами!
            </p>

            <div className="space-y-3">
              {personalities.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    haptic('selection');
                    setPersonality(p.id);
                  }}
                  className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all duration-200 ${
                    personality === p.id
                      ? 'glass-circle-green'
                      : 'glass-circle'
                  }`}
                  style={personality === p.id ? { borderColor: 'rgba(34, 197, 94, 0.5)' } : {}}
                >
                  <div className="w-12 h-12 rounded-full glass-circle flex items-center justify-center">
                    <span className="text-2xl">{p.emoji}</span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{p.name}</p>
                    <p className="text-sm text-tg-hint">{p.desc}</p>
                  </div>
                  {personality === p.id && (
                    <Check className="w-6 h-6 text-plant-green-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fallback bottom button for non-Telegram browsers */}
      {!isTelegramWebApp && (step === 'details' || step === 'personality') && (
        <div className="px-4 py-4 border-t border-[var(--sf-border-light)] safe-area-bottom">
          <button
            onClick={step === 'details' ? handleDetailsNext : handleSubmit}
            disabled={!nickname.trim() || isSubmitting}
            className="tg-button w-full flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <LoadingSpinner size="sm" />
            ) : step === 'details' ? (
              <>
                <span>Далее</span>
                <ChevronRight className="w-5 h-5" />
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>{isEditMode ? 'Сохранить' : 'Добавить растение'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
